import React, { useState, useMemo, useRef, useEffect } from 'react';
import triangleDownIcon from '../../../../assets/images/triangle-down.svg';
import '../Styles/MultiSelectDropdown.scss';

const MultiSelectDropdown = ({
    label,
    options = [],
    selectedValues = [],
    onSelectionChange,
    placeholder = 'Chọn...',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const displayRef = useRef(null);
    const modalRef = useRef(null);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, selectedValues]);

    // Tính toán vị trí modal khi mở
    useEffect(() => {
        if (isOpen && displayRef.current) {
            const updatePosition = () => {
                if (displayRef.current) {
                    const rect = displayRef.current.getBoundingClientRect();
                    
                    let top = rect.bottom + 8;
                    let left = rect.left;
                    const width = rect.width;  
                    const viewportHeight = window.innerHeight;
                    const modalHeight = 460; 
                    const spaceBelow = viewportHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    if (spaceBelow < modalHeight && spaceAbove > spaceBelow) {
                        top = rect.top - modalHeight - 8;
                    }
                    
                    // Đảm bảo modal không vượt quá viewport
                    if (top + modalHeight > viewportHeight) {
                        top = Math.max(8, viewportHeight - modalHeight - 8);
                    }
                    if (top < 0) {
                        top = 8;
                    }
                    
                    setModalPosition({ top, left, width });
                }
            };
            
            requestAnimationFrame(() => {
                updatePosition();
            });
            
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen]);

    const selectableOptions = useMemo(
        () => options.filter((option) => option.value !== -1),
        [options],
    );
    const selectableValues = useMemo(
        () => selectableOptions.map((opt) => opt.value),
        [selectableOptions],
    );

    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) {
            return selectableOptions;
        }
        const term = searchTerm.toLowerCase();
        return selectableOptions.filter((option) =>
            option.label.toLowerCase().includes(term)
        );
    }, [selectableOptions, searchTerm]);

    const effectiveSelectedValues = useMemo(() => {
        if (selectedValues.length === 1 && selectedValues[0] === -1) {
            return selectableValues;
        }
        return selectedValues.filter((val) => val !== -1);
    }, [selectedValues, selectableValues]);

    // Đếm số lượng đã chọn (không tính -1)
    const selectedCount = effectiveSelectedValues.length;
    const remainingCount = selectableOptions.length - selectedCount;

    const handleToggleItem = (value) => {
        const baseSelected =
            selectedValues.length === 1 && selectedValues[0] === -1
                ? selectableValues
                : selectedValues.filter((val) => val !== -1);

        const exists = baseSelected.includes(value);
        const nextSelected = exists
            ? baseSelected.filter((v) => v !== value)
            : [...baseSelected, value];

        if (nextSelected.length === selectableValues.length && selectableValues.length > 0) {
            onSelectionChange([-1]);
        } else {
            onSelectionChange(nextSelected);
        }
    };

    // Kiểm tra xem đã chọn tất cả (không tính -1)
    const isAllSelected = useMemo(() => {
        const validSelected = selectedValues.filter((val) => val !== -1);
        return selectableValues.length > 0 && validSelected.length === selectableValues.length;
    }, [selectedValues, selectableValues.length]);

    const getDisplayItems = () => {
        if (selectedValues.length === 0) {
            return { type: 'none', items: [] };
        }
        // Nếu selectedValues = [-1] hoặc đã chọn hết, hiển thị "Tất cả"
        if (
            (selectedValues.length === 1 && selectedValues[0] === -1) ||
            isAllSelected
        ) {
            return { type: 'all', items: [{ label: 'Tất cả', value: -1 }] };
        }

        const selectedItems = selectedValues
            .filter((val) => val !== -1)
            .map((val) => {
                const opt = options.find((o) => o.value === val);
                return opt ? { label: opt.label, value: opt.value } : null;
            })
            .filter((item) => !!item);

        if (selectedItems.length === 0) {
            return { type: 'count', items: [{ label: `Đã chọn ${selectedValues.length}`, value: null }] };
        }

        return { type: 'list', items: selectedItems };
    };

    const handleClearSelection = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onSelectionChange([]);
    };

    const handleToggleAll = (shouldSelectAll) => {
        if (shouldSelectAll) {
            onSelectionChange([-1]);
        } else {
            onSelectionChange([]);
        }
    };

    // Kiểm tra xem đã chọn tất cả trong modal (không tính -1)
    const isAllSelectedInModal = useMemo(() => {
        return (
            selectableValues.length > 0 &&
            effectiveSelectedValues.length === selectableValues.length
        );
    }, [effectiveSelectedValues, selectableValues.length]);

    return (
        <div className="multi-select-dropdown" ref={dropdownRef}>
            <div className={`multi-select-field ${selectedValues.length > 0 ? 'has-selection' : ''}`}>
                <label className={selectedValues.length === 0 ? 'no-selection' : ''}>{label}</label>
                <div
                    ref={displayRef}
                    className={`multi-select-display ${isOpen ? 'is-open' : ''}`}
                    onClick={() => {
                        if (!isOpen) {
                            setSearchTerm('');
                        }
                        setIsOpen(!isOpen);
                    }}
                >
                    {(() => {
                        const display = getDisplayItems();
                        if (display.type === 'none') {
                            return <span className="display-text"></span>;
                        }
                        if (display.type === 'all' || display.type === 'count') {
                            return (
                                <div className="multi-select-chip" onClick={(e) => e.stopPropagation()}>
                                    <span>{display.items[0].label}</span>
                                    <button
                                        type="button"
                                        className="chip-remove"
                                        onClick={handleClearSelection}
                                        aria-label="Xóa lựa chọn"
                                    >
                                    </button>
                                </div>
                            );
                        }
                        // type === 'list' : hiển thị từng item riêng
                        return (
                            <div className="chip-list" onClick={(e) => e.stopPropagation()}>
                                {display.items.map((item) => (
                                    <div key={item.value} className="multi-select-chip">
                                        <span>{item.label}</span>
                                        <button
                                            type="button"
                                            className="chip-remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleItem(item.value);
                                            }}
                                            aria-label={`Xóa ${item.label}`}
                                        >
                                        </button>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                    <img
                        src={triangleDownIcon}
                        alt=""
                        aria-hidden="true"
                        className="chevron-icon"
                    />
                </div>
            </div>

            {isOpen && modalPosition.width > 0 && (
                <div 
                    ref={modalRef}
                    className="multi-select-modal"
                    style={{
                        position: 'fixed',
                        top: `${modalPosition.top}px`,
                        left: `${modalPosition.left}px`,
                        width: `${modalPosition.width}px`,
                    }}
                >
                    <div className="modal-search">
                        <input
                            type="text"
                            placeholder="Tìm kiếm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="modal-scrollable-content">
                        <div className="modal-summary-panel">
                            <div className="modal-selection-summary">
                                <button
                                    type="button"
                                    className="selection-info"
                                    role="checkbox"
                                    aria-checked={isAllSelectedInModal}
                                    onClick={() => handleToggleAll(!isAllSelectedInModal)}
                                >
                                    <span
                                        className={`select-all-toggle ${isAllSelectedInModal ? 'is-checked' : ''}`}
                                        aria-hidden="true"
                                    ></span>
                                    <span className="selection-info__label">
                                        {isAllSelectedInModal
                                            ? 'Tất cả'
                                            : `Đã chọn ${selectedCount}`}
                                    </span>
                                    <img
                                        src={triangleDownIcon}
                                        alt=""
                                        aria-hidden="true"
                                        className="summary-chevron"
                                    />
                                </button>
                            </div>

                            <div className="modal-remaining">
                                <span>Còn lại {remainingCount}</span>
                            </div>
                        </div>

                        <div className="modal-list">
                        {filteredOptions.length === 0 ? (
                            <div className="empty-list">Không tìm thấy kết quả</div>
                        ) : (
                            filteredOptions.map((option) => {
                                const isChecked = effectiveSelectedValues.includes(option.value);
                                return (
                                    <label
                                        key={option.value}
                                        className={`modal-list-item ${isChecked ? 'is-selected' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleToggleItem(option.value)}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                );
                            })
                        )}
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;

